import React, { useEffect, useMemo, useState } from "react";
import Page from "../../components/Page";
import {
  UserCheck,
  Check,
  X,
  Minus,
  Save,
  CheckCheck,
  AlertCircle,
  Shirt,
  Sparkles,
  HardHat,
  Gem,
  DoorClosed,
  ChevronRight,
} from "lucide-react";

/**
 * ChecklistIgienePersonale
 * LOGICA AGGIORNATA:
 * - 6 voci
 * - conforme | nonconforme | na
 * - Data/ora auto al salvataggio
 * - Se almeno una voce è "nonconforme": modale + nota correttiva obbligatoria
 * - NESSUNA FOTO RICHIESTA
 * - Salva su /api/checklist_igiene_personale.php (ritorna {success, id})
 * - Mostra "Ultime 5 registrazioni" via /api/checklist_igiene_personale_list.php
 */

async function fetchJSON(url, options) {
  const res = await fetch(url, options);
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    const preview = text ? text.slice(0, 180) : "(vuoto)";
    throw new Error(
      `Risposta non valida dal server (${res.status}). Anteprima: ${preview}`
    );
  }
  if (!res.ok) {
    const msg =
      (data && (data.error || data.message)) || `Errore HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

function toSQLDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toSQLTime(d) {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function normalizeErr(e) {
  const msg = e?.message || "Errore imprevisto";
  if (/Non autenticato/i.test(msg) || /401/.test(msg)) {
    return "Sessione scaduta o non autenticato. Esegui il login e riprova.";
  }
  return msg;
}

const ANSWERS = {
  conforme: { label: "Conforme" },
  nonconforme: { label: "Non conf." },
  na: { label: "N.A." },
};

function fmtDateShort(yyyy_mm_dd) {
  if (!yyyy_mm_dd) return "—";
  if (/^\d{2}\/\d{2}\/\d{4}/.test(yyyy_mm_dd)) return yyyy_mm_dd;
  const [y, m, d] = String(yyyy_mm_dd).slice(0, 10).split("-");
  if (!y || !m || !d) return String(yyyy_mm_dd);
  return `${d}/${m}/${y}`;
}

export default function ChecklistIgienePersonale({ reparto = "" }) {
  const items = useMemo(
    () => [
      {
        key: "item_abbigliamento",
        title: "Abbigliamento idoneo",
        desc: "L’operatore indossa abbigliamento aziendale oppure indumenti di colore chiaro.",
        icon: Shirt,
      },
      {
        key: "item_uso_lavoro",
        title: "Uso solo sul posto di lavoro",
        desc: "L’abbigliamento è utilizzato solo sul posto di lavoro e riposto nell’armadietto a fine turno.",
        icon: Sparkles,
      },
      {
        key: "item_pulizia",
        title: "Pulizia e buone condizioni",
        desc: "Gli indumenti sono puliti, in buone condizioni e sostituiti con frequenza adeguata.",
        icon: Sparkles,
      },
      {
        key: "item_cuffia",
        title: "Cuffia / copricapo",
        desc: "Cuffia o copricapo indossati correttamente; capelli lunghi legati; niente forcine/fermagli non fissati.",
        icon: HardHat,
      },
      {
        key: "item_gioielli",
        title: "Assenza di gioielli",
        desc: "Anelli, bracciali, collane, spille, orecchini ecc. lasciati nell’armadietto.",
        icon: Gem,
      },
      {
        key: "item_armadietto",
        title: "Armadietto in ordine",
        desc: "Armadietto pulito e ordinato; niente abbigliamento/scarpe fuori; parte superiore non usata come deposito.",
        icon: DoorClosed,
      },
    ],
    []
  );

  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(items.map((i) => [i.key, null]))
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [noteCorrettive, setNoteCorrettive] = useState("");

  function setAnswer(key, value) {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }

  const allAnswered = useMemo(
    () => items.every((i) => answers[i.key] !== null),
    [answers, items]
  );

  const hasNonConforme = useMemo(
    () => items.some((i) => answers[i.key] === "nonconforme"),
    [answers, items]
  );

  const esitoLabel = useMemo(() => {
    if (!allAnswered) return "Da completare";
    return hasNonConforme ? "Non conforme" : "Conforme";
  }, [allAnswered, hasNonConforme]);

  const progressCount = useMemo(() => {
    return items.reduce((acc, it) => {
      const v = answers[it.key];
      if (v && v !== "na") return acc + 1;
      return acc;
    }, 0);
  }, [answers, items]);

  const ringCirc = 125.6;
  const ringOffset = useMemo(() => {
    const total = items.length || 1;
    return ringCirc - (ringCirc * progressCount) / total;
  }, [progressCount, items.length]);

  useEffect(() => {
    loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadRecent() {
    try {
      setLoadingRecent(true);
      setError(null);
      const qs = reparto ? `?reparto=${encodeURIComponent(reparto)}` : "";
      const js = await fetchJSON(
        `/api/checklist_igiene_personale_list.php${qs}`,
        { credentials: "include" }
      );
      if (!js?.success) {
        throw new Error(js?.error || "Impossibile leggere lo storico");
      }
      setRecent(Array.isArray(js.records) ? js.records : []);
    } catch (e) {
      setError(normalizeErr(e));
    } finally {
      setLoadingRecent(false);
    }
  }

  function mapAnswerToDb(v) {
    if (v === "conforme") return 1;
    if (v === "nonconforme") return 0;
    return null;
  }

  async function doSubmit({ confirmed }) {
    try {
      setSaving(true);
      setError(null);

      if (!allAnswered) {
        throw new Error("Completa la checklist prima di salvare.");
      }

      if (hasNonConforme && !noteCorrettive.trim()) {
        throw new Error(
          "Inserisci l’azione correttiva (obbligatoria in caso di non conformità)."
        );
      }

      const now = new Date();
      const payload = {
        reparto: reparto || "",
        data: toSQLDate(now),
        ora: toSQLTime(now),
        confermato: confirmed ? 1 : 0,
        note_correttive: hasNonConforme ? noteCorrettive.trim() : "",
      };

      for (const i of items) {
        payload[i.key] = mapAnswerToDb(answers[i.key]);
      }

      const js = await fetchJSON("/api/checklist_igiene_personale.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!js?.success) {
        throw new Error(js?.error || "Impossibile salvare la checklist");
      }

      setAnswers(Object.fromEntries(items.map((i) => [i.key, null])));
      setNoteCorrettive("");
      setConfirmOpen(false);

      await loadRecent();
    } catch (e) {
      setError(normalizeErr(e));
    } finally {
      setSaving(false);
    }
  }

  function handleSave() {
    setError(null);
    if (!allAnswered) {
      setError("Completa la checklist prima di salvare.");
      return;
    }
    if (hasNonConforme) {
      setConfirmOpen(true);
    } else {
      doSubmit({ confirmed: true });
    }
  }

  function markAllConforme() {
    setError(null);
    setAnswers((prev) => {
      const next = { ...prev };
      for (const it of items) next[it.key] = "conforme";
      return next;
    });
  }

  function resetAll() {
    setAnswers(Object.fromEntries(items.map((i) => [i.key, null])));
    setNoteCorrettive("");
    setError(null);
  }

  const statusPill = (() => {
    if (esitoLabel === "Conforme") {
      return "bg-green-100 text-green-700";
    }
    if (esitoLabel === "Non conforme") {
      return "bg-red-100 text-red-700";
    }
    return "bg-gray-100 text-gray-600";
  })();

  return (
    <Page title="Checklist Igiene Personale" back>
      <div className="pb-6">
        <div className="bg-white border border-gray-100 shadow-sm rounded-2xl px-4 md:px-6 py-4 mb-6">
          <div className="flex items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#003B71] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-900/20">
                <UserCheck className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[#003B71] text-xl md:text-2xl font-bold leading-none">
                  Norma
                </div>
                <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold hidden sm:block">
                  Food Safety Pilot
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusPill}`}
              >
                {esitoLabel}
              </span>
              {!allAnswered ? (
                <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">
                  Mancano risposte
                </span>
              ) : null}
            </div>
          </div>

          <div className="mt-3">
            <div className="text-2xl md:text-3xl text-gray-800 font-light tracking-tight">
              Checklist Igiene Personale{" "}
              {reparto ? (
                <span className="text-[#003B71] font-medium">({reparto})</span>
              ) : null}
            </div>
            <p className="text-gray-400 text-sm font-light mt-1">
              Compila a inizio turno. Data e ora vengono salvate automaticamente.
              In caso di <b>non conformità</b> è richiesta l’azione correttiva.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <section className="lg:col-span-8 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-[24px] p-4 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="relative w-12 h-12 flex items-center justify-center">
                  <svg className="transform -rotate-90 w-12 h-12">
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      stroke="#F3F4F6"
                      strokeWidth="4"
                      fill="transparent"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      stroke={hasNonConforme ? "#EF4444" : "#059669"}
                      strokeWidth="4"
                      fill="transparent"
                      strokeDasharray={ringCirc}
                      strokeDashoffset={ringOffset}
                      className="transition-all duration-700 ease-out"
                    />
                  </svg>
                  <span className="absolute text-xs font-bold text-gray-700">
                    {progressCount}/{items.length}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-800">Avanzamento</div>
                  <div className="text-xs text-gray-400">
                    Conteggio esclusi i N.A.
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={markAllConforme}
                  disabled={saving}
                  className="px-5 py-2.5 rounded-[16px] bg-[#E0F7F2] text-[#008F70] text-sm font-bold hover:bg-[#008F70] hover:text-white transition-all flex items-center gap-2 disabled:opacity-60"
                >
                  <CheckCheck className="w-4 h-4" />
                  Segna tutto conforme
                </button>

                <button
                  type="button"
                  onClick={resetAll}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-[16px] bg-gray-50 text-gray-600 text-sm font-bold hover:bg-gray-100 transition-all disabled:opacity-60"
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {items.map((it, idx) => {
                const v = answers[it.key];
                const Icon = it.icon || Shirt;

                const baseBtn =
                  "flex-1 md:flex-none px-4 py-2.5 rounded-[14px] text-xs font-bold flex flex-col items-center justify-center gap-1 transition-all border";
                const inactive =
                  "bg-gray-50 text-gray-400 hover:text-gray-600 border-transparent hover:border-gray-200";
                const activeOk =
                  "bg-[#059669] text-white shadow-md border-transparent transform scale-[1.03]";
                const activeKo =
                  "bg-[#EF4444] text-white shadow-md border-transparent transform scale-[1.03]";
                const activeNa =
                  "bg-gray-500 text-white shadow-md border-transparent transform scale-[1.03]";

                return (
                  <div
                    key={it.key}
                    className="bg-white rounded-[24px] p-6 shadow-sm border border-gray-100 relative transition-all hover:shadow-md"
                  >
                    <div className="flex flex-col md:flex-row gap-4 md:items-start">
                      <div className="w-12 h-12 rounded-[18px] bg-[#F0F9FF] text-[#003B71] flex items-center justify-center shrink-0">
                        <Icon className="w-6 h-6" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-1">
                              {it.title}
                            </h3>
                            <p className="text-sm text-gray-400 font-light leading-relaxed">
                              {it.desc}
                            </p>
                          </div>

                          <div className="hidden md:flex items-center gap-2">
                            {v ? (
                              <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700">
                                {ANSWERS[v]?.label || v}
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-50 text-gray-400 border border-gray-100">
                                Da fare
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 flex gap-2 shrink-0 w-full md:w-auto">
                          <button
                            type="button"
                            onClick={() => setAnswer(it.key, "conforme")}
                            className={[
                              baseBtn,
                              "min-w-[70px]",
                              v === "conforme" ? activeOk : inactive,
                            ].join(" ")}
                          >
                            <Check className="w-4 h-4" />
                            Conforme
                          </button>

                          <button
                            type="button"
                            onClick={() => setAnswer(it.key, "nonconforme")}
                            className={[
                              baseBtn,
                              "min-w-[70px]",
                              v === "nonconforme" ? activeKo : inactive,
                            ].join(" ")}
                          >
                            <X className="w-4 h-4" />
                            Non conf.
                          </button>

                          <button
                            type="button"
                            onClick={() => setAnswer(it.key, "na")}
                            className={[
                              baseBtn,
                              "min-w-[56px]",
                              v === "na" ? activeNa : inactive,
                            ].join(" ")}
                          >
                            <Minus className="w-4 h-4" />
                            N.A.
                          </button>
                        </div>

                        <div
                          className={[
                            "mt-4 border-t border-gray-100 overflow-hidden transition-all duration-300",
                            v === "nonconforme"
                              ? "max-h-[140px] opacity-100 pt-4"
                              : "max-h-0 opacity-0 pt-0",
                          ].join(" ")}
                        >
                          <div className="grid grid-cols-1 gap-4">
                            <div className="rounded-[16px] bg-red-50/40 border border-red-100 p-4">
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Azione correttiva
                              </div>
                              <div className="text-sm text-gray-700">
                                Verrà richiesta in fase di salvataggio.
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="md:hidden mt-3">
                          {v ? (
                            <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700">
                              {ANSWERS[v]?.label || v}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="absolute top-4 right-4 text-xs text-gray-300 font-mono">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#003B71] hover:bg-[#002B5B] text-white font-bold text-lg py-4 rounded-[20px] shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 disabled:opacity-60 disabled:transform-none"
            >
              <Save className="w-6 h-6" />
              {saving ? "Salvataggio..." : "Salva Checklist"}
            </button>

            {error && (
              <div className="rounded-[20px] border border-red-100 bg-red-50/40 px-4 py-3 text-red-700 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 mt-0.5" />
                <div className="text-sm">{error}</div>
              </div>
            )}
          </section>

          <section className="lg:col-span-4 space-y-6">
            <div className="bg-white rounded-[24px] p-6 shadow-sm border border-gray-100">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-800">
                  Ultime 5 registrazioni
                </h3>

                <a
                  href={`/verifica-igiene-personale.php${reparto ? `?reparto=${encodeURIComponent(reparto)}` : ""
                    }`}
                  className="text-xs text-[#003B71] font-bold hover:underline uppercase tracking-wide flex items-center gap-1"
                >
                  Vedi tutto <ChevronRight className="w-4 h-4" />
                </a>
              </div>

              <div className="space-y-4">
                {loadingRecent ? (
                  <div className="p-4 rounded-[20px] border border-gray-100 bg-gray-50 text-gray-500 text-sm">
                    Caricamento…
                  </div>
                ) : recent.length === 0 ? (
                  <div className="p-4 rounded-[20px] border border-gray-100 bg-gray-50 text-gray-500 text-sm">
                    Nessuna registrazione trovata.
                  </div>
                ) : (
                  recent.map((r) => {
                    const isKo = r.esito === "NON_CONFORME";
                    return (
                      <div
                        key={r.id}
                        className={[
                          "p-4 rounded-[20px] border transition-all group",
                          isKo
                            ? "border-red-100 bg-red-50/30 hover:bg-red-50/60"
                            : "border-gray-100 hover:bg-gray-50",
                        ].join(" ")}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-bold text-gray-800">
                            {fmtDateShort(r.data)}
                          </div>
                          <span className="text-xs font-mono text-gray-400">
                            {r.ora || "—"}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            {r.reparto || "—"}
                          </span>

                          {isKo ? (
                            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Non conf.
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                              <Check className="w-3 h-3" /> Conforme
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </section>
        </div>

        {confirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-3">
            <div className="bg-white rounded-[24px] shadow-2xl p-5 w-full max-w-lg border border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    Sono presenti non conformità
                  </h3>
                  <p className="text-gray-400 text-sm font-light mt-1">
                    Prima di salvare, indica l’azione correttiva.
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-600">
                  <AlertCircle className="w-5 h-5" />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Azione correttiva
                </label>
                <textarea
                  className="w-full min-h-[96px] rounded-[16px] border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#EF4444]"
                  placeholder="Es: sostituito abbigliamento, rimossi gioielli, indossata cuffia correttamente…"
                  value={noteCorrettive}
                  onChange={(e) => setNoteCorrettive(e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2.5 rounded-[16px] bg-gray-50 text-gray-700 font-bold text-sm hover:bg-gray-100 transition-all disabled:opacity-60"
                  onClick={() => setConfirmOpen(false)}
                  disabled={saving}
                >
                  Annulla
                </button>

                <button
                  type="button"
                  className="px-4 py-2.5 rounded-[16px] bg-[#EF4444] text-white font-bold text-sm hover:bg-[#DC2626] transition-all disabled:opacity-60"
                  onClick={() => doSubmit({ confirmed: true })}
                  disabled={saving}
                >
                  {saving ? "Salvo…" : "Conferma e salva"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}